import api, { handleApiResponse, handleApiError } from './api';

class CategoryService {
  // Get all categories (admin view)
  async getCategories() {
    try {
      const response = await api.get('/categories');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get categories for ticket creation (public endpoint for authenticated users)
  async getCategoriesForTickets() {
    try {
      const response = await api.get('/tickets/categories');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get subcategories for tickets
  async getSubcategoriesForTickets(categoryId) {
    try {
      const response = await api.get(`/tickets/categories/${categoryId}/subcategories`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get category tree
  async getCategoryTree() {
    try {
      const response = await api.get('/categories/tree');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get single category
  async getCategory(id) {
    try {
      const response = await api.get(`/categories/${id}`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Create category
  async createCategory(data) {
    try {
      const response = await api.post('/categories', data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Update category
  async updateCategory(id, data) {
    try {
      const response = await api.put(`/categories/${id}`, data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Delete category
  async deleteCategory(id, force = false) {
    try {
      const response = await api.delete(`/categories/${id}`, {
        params: { force }
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Toggle category status
  async toggleStatus(id) {
    try {
      const response = await api.patch(`/categories/${id}/toggle-status`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get subcategories
  async getSubcategories(id) {
    try {
      const response = await api.get(`/categories/${id}/subcategories`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get experts for category
  async getExpertsForCategory(categoryId) {
    try {
      const response = await api.get(`/categories/${categoryId}/experts`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Find best agent for category
  async findBestAgent(categoryId, data = {}) {
    try {
      const response = await api.post(`/categories/${categoryId}/find-best-agent`, data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Utility methods
  buildCategoryTree(categories) {
    const categoryMap = new Map();
    const rootCategories = [];

    // First pass: create all category objects
    categories.forEach(cat => {
      categoryMap.set(cat.id, { ...cat, children: [] });
    });

    // Second pass: build the tree structure
    categories.forEach(cat => {
      if (cat.parent_id) {
        const parent = categoryMap.get(cat.parent_id);
        if (parent) {
          parent.children.push(categoryMap.get(cat.id));
        }
      } else {
        rootCategories.push(categoryMap.get(cat.id));
      }
    });

    return rootCategories;
  }

  flattenCategoryTree(categories, level = 0) {
    const flattened = [];

    categories.forEach(category => {
      flattened.push({
        ...category,
        level,
        indentedName: '  '.repeat(level) + category.name
      });

      if (category.children && category.children.length > 0) {
        flattened.push(...this.flattenCategoryTree(category.children, level + 1));
      }
    });

    return flattened;
  }

  // Get category path string
  getCategoryPath(category, categories) {
    if (!category.parent_id) {
      return category.name;
    }

    const parent = categories.find(cat => cat.id === category.parent_id);
    if (!parent) {
      return category.name;
    }

    return `${this.getCategoryPath(parent, categories)} > ${category.name}`;
  }

  // Validate category data
  validateCategoryData(data) {
    const errors = {};

    if (!data.name || !data.name.trim()) {
      errors.name = 'Category name is required';
    } else if (data.name.trim().length < 2) {
      errors.name = 'Category name must be at least 2 characters';
    } else if (data.name.trim().length > 255) {
      errors.name = 'Category name must be less than 255 characters';
    }

    if (data.description && data.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Check if category can be deleted
  canDeleteCategory(category) {
    return !category.children || category.children.length === 0;
  }

  // Format category for display
  formatCategoryForDisplay(category) {
    return {
      ...category,
      has_children: category.children && category.children.length > 0,
      is_leaf: !category.children || category.children.length === 0,
      level_display: category.level ? `Level ${category.level}` : 'Root Level',
      status_display: category.is_active ? 'Active' : 'Inactive'
    };
  }
}

export default new CategoryService();